// GENERATED FROM mobile_charts@647b6df lib/oi-studies.js
// sha256=344ba91850eed0f85b2e836dcc163c92a1688161a65f98e1a76577304ab5bd2d
// DO NOT EDIT HERE. Edit the mobile_charts original and re-run
// scripts/sync-vendored.sh — the catalogue of studies that read open interest decides which requests carry needs_oi. A name in one product and not the other means that study silently plots nothing on one surface, and the failure is indistinguishable from a symbol that has no OI.
// Does this chart need the open-interest column?
//
// `needs_oi` on /data/history is not a build capability, it is a question about
// what is on the chart RIGHT NOW. The column is dead weight on every chart that
// has none of the OI studies applied — which is nearly every chart — and the
// studies cannot work without it. So the request asks for it exactly when
// something is there to read it, and this module is what answers that.
//
// SHARED, AND VENDORED. flow-web gets a generated copy of this file via
// scripts/sync-vendored.sh. The catalogue below has to be the same list in both
// products or one of them silently stops asking for OI on a study the other
// serves, and that failure looks identical to "this symbol has no open
// interest" — see _warnIfNoOi in udf-compatible-datafeed-base.js.
//
// WHY THE LIST IS HERE AND NOT IMPORTED FROM THE PACK. @rupeezy/chart-indicators
// owns the authoritative catalogue in study-hints.js (CHART_REQUIREMENT), but
// its package.json `exports` map publishes only "." and "./bar-store.js", so
// production code cannot reach it. Rather than release the pack for a read-only
// list, the list is copied here and test/oi-studies.test.mjs imports the pack's
// study-hints.js BY RELATIVE PATH (which the exports gate does not apply to) and
// asserts the two agree. Add an eleventh OI study to the pack and that test goes
// red on the next install.

/**
 * The studies that read the oi column off a history bar.
 *
 * TEN, NOT ELEVEN. The seven chain studies — OI Profile, Max Pain, OI Support
 * Resistance, PCR (Open Interest), ATM Straddle Price, Total Chain OI, Basis —
 * have "OI" in the name or in the description and read the option-chain
 * endpoint instead. They work with `needs_oi` off and must not be listed here,
 * or every chart with a Max Pain on it asks for a column it never reads.
 */
export const OI_STUDY_NAMES = [
    'Open Interest',
    'OI Change',
    'Day OI Change',
    'Price OI Buildup',
    'OI Rate of Change',
    'OI Spurt',
    'Volume OI Ratio',
    'OI Weighted VWAP',
    'Price OI Divergence',
    'Futures OI Levels',
];

const OI_STUDIES = new Set(OI_STUDY_NAMES);

/** The pack's branding separator. Keep in step with chart-indicators' common.js. */
const BRAND_MARK = '◆';

/**
 * A study's bare name, as the catalogue spells it.
 *
 * getAllStudies() reports metainfo.description, and the pack brands every study
 * it registers — "Open Interest ◆ Rupeezy Exclusive". Cut at the mark.
 *
 * `cut > 0`, not `cut !== -1`, copying the pack's own shape: a description that
 * somehow STARTS with the mark has no name in front of it, and slicing to an
 * empty string would match nothing anyway.
 */
export function bareStudyName(description) {
    const text = String(description);
    const cut = text.indexOf(BRAND_MARK);
    return (cut > 0 ? text.slice(0, cut) : text).trim();
}

/**
 * Is a study that reads OI applied to ANY chart in the layout?
 *
 * EVERY CHART, not activeChart(). One datafeed serves the whole layout, so a
 * getBars triggered by chart 1 must carry the column when the OI study is
 * sitting on chart 2. (The pack's own refreshWarmUpStudies is activeChart()-only
 * — that is the narrower job of nudging one study to recompute, not deciding
 * what a shared request asks for.)
 *
 * A chart that cannot be read counts as having no OI study, rather than hiding
 * the charts after it: under-asking on one chart mid-teardown costs nothing,
 * and the next event re-reads the whole layout anyway.
 */
export function chartWantsOi(widget) {
    if (!widget) {
        return false;
    }
    let count = 1;
    try {
        if (typeof widget.chartsCount === 'function') {
            count = widget.chartsCount();
        }
    } catch (error) {
        count = 1;
    }
    for (let i = 0; i < count; i++) {
        let studies;
        try {
            studies = widget.chart(i).getAllStudies();
        } catch (error) {
            // One chart mid-teardown, or an index past a layout that just
            // shrank, must not hide the studies on the others.
            continue;
        }
        // Same reason, for a chart that answers without throwing but with
        // nothing iterable. Unguarded, the loop below would throw out of the
        // sweep's timer.
        if (!Array.isArray(studies)) {
            continue;
        }
        for (const entry of studies) {
            if (OI_STUDIES.has(bareStudyName(entry && entry.name))) {
                return true;
            }
        }
    }
    return false;
}

/**
 * Watch the layout and report when the answer changes.
 *
 * `onChange(wanted)` fires only on a TRANSITION, never on every event, so the
 * caller can treat it as "the wire contract just changed" and refetch on the
 * rising edge without any throttle of its own.
 *
 * THREE SUBSCRIPTIONS, AND ALL THREE ARE LOAD-BEARING:
 *   study_event    — a study added or removed by hand.
 *   chart_loaded   — every layout load, including the one load_last_chart
 *                    applies at boot, after onChartReady. It is the event both
 *                    hosts already re-read the layout on (mobile_charts'
 *                    trade-line re-render, flow-web's multiChartLayout.ts). A
 *                    returning user's saved OI study arrives this way, not as a
 *                    study_event.
 *   layout_changed — a chart hidden by a smaller layout is not reachable through
 *                    widget.chart(i), so its studies are invisible to the sweep.
 *                    Growing the layout back reveals them with no study_event
 *                    (nothing was created) and no chart_loaded (nothing was
 *                    loaded).
 */
export function watchOiStudies(options) {
    const widget = options && options.widget;
    const onChange = (options && options.onChange) || function () {};
    const log = (options && options.log) || function () {};
    const EVENTS = ['study_event', 'chart_loaded', 'layout_changed'];

    let wanted = false;
    let started = false;
    let timer = null;
    const handlers = [];

    function sweep() {
        timer = null;
        const next = chartWantsOi(widget);
        // THE ENTIRE LOOP GUARD, and it needs nothing else. The predicate is a
        // pure function of the study set; a refetch cannot add or remove a
        // study, so the sweep that a refetch provokes takes this early return.
        // (Triggering on "the response carried no oi_c" instead would never
        // settle — an index legitimately has none, and would be refetched
        // forever.)
        if (next === wanted) {
            return;
        }
        wanted = next;
        // Contained, because this runs in a timer: a throw from the host (its
        // refetch, say) would otherwise surface as an uncaught error with no
        // trail back to the OI study that was left without its column.
        try {
            onChange(next);
        } catch (error) {
            log('warn', 'chart.oi.on_change_failed', {
                wanted: next,
                error: (error && error.message) || String(error),
            });
        }
    }

    /**
     * A MACROTASK, and the difference is real. The pack's duplicate-study guard
     * removes a repeat add inside a microtask (chart-indicators study-events.js),
     * and microtasks drain before timers — so a setTimeout(0) sweep reads the
     * settled study set, while a Promise.resolve().then() one can read the
     * duplicate that is about to vanish and spend a refetch on it.
     *
     * It also coalesces: three OI studies added from a still-open Indicators
     * dialog collapse into one sweep, and the transition check above collapses
     * them again into one refetch.
     */
    function schedule() {
        if (timer !== null) {
            return;
        }
        timer = setTimeout(sweep, 0);
    }

    return {
        start() {
            if (started || !widget || typeof widget.subscribe !== 'function') {
                return false;
            }
            started = true;
            for (const event of EVENTS) {
                // The arguments are ignored on purpose. Every one of these
                // events asks the same question — "what is on the layout now" —
                // so re-reading it covers every study_event type (create,
                // remove, paste_study) without enumerating them.
                const handler = () => schedule();
                try {
                    widget.subscribe(event, handler);
                    handlers.push({ event, handler });
                } catch (error) {
                    log('warn', 'chart.oi.subscribe_failed', {
                        event,
                        error: (error && error.message) || String(error),
                    });
                }
            }
            // A study can already be on the chart before the first event ever
            // fires — a restored layout, or an attach that happens late.
            schedule();
            return true;
        },

        stop() {
            if (timer !== null) {
                clearTimeout(timer);
                timer = null;
            }
            if (!started) {
                return false;
            }
            started = false;
            for (const entry of handlers) {
                try {
                    if (typeof widget.unsubscribe === 'function') {
                        widget.unsubscribe(entry.event, entry.handler);
                    }
                } catch (error) { /* a torn-down widget owes us nothing */ }
            }
            handlers.length = 0;
            return true;
        },
    };
}
